/**
 * Link-sharing single source of truth.
 *
 * Every share in the app builds its URL and copy through this module so that:
 *  - shared URLs always match a canonical, prerender-resolvable path
 *    (`/post/{id}`, `/book/{id}`, `/author/{slug}-{id}`) — the pretty
 *    `/poem/{author}/{title}-{id}` links this module replaces could not be
 *    resolved server-side, so link previews said "Page not found";
 *  - the share text is identical everywhere; and
 *  - the execution chain (Android WebView bridge → Web Share API → clipboard)
 *    is implemented once and reports an accurate outcome to the caller.
 */
import { getAuthorSlug, getPostSlug, getBookSlug, slugify } from './authorSlug';
import { cleanAuthorName, getAuthorDisplayName } from '../types';
import { isAndroidWebView, vibrateNative } from './mobileBridge';

export const SITE_TAGLINE = 'The Home for Literature & Writers';

export interface ShareData {
  title: string;
  text?: string;
  url: string;
  author?: string;
  coverUrl?: string;
  type?: 'book' | 'post' | 'poem' | 'article' | 'essay';
}

/** 'dismissed' means the user closed the native sheet — never toast on it. */
export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

interface PostShareInput {
  id: string;
  title?: string | null;
  author_name?: string | null;
  content?: string | null;
  type?: string | null;
  background_url?: string | null;
  media_url?: string | null;
  display_name?: string | null;
  full_name?: string | null;
  name_display_preference?: 'display_name' | 'full_name' | string | null;
  profiles?: any;
}

interface BookShareInput {
  id: string;
  title?: string | null;
  author_name?: string | null;
  summary?: string | null;
  description?: string | null;
  cover_url?: string | null;
  series_title?: string | null;
  series_order?: number | null;
  display_name?: string | null;
  full_name?: string | null;
  name_display_preference?: 'display_name' | 'full_name' | string | null;
}

function origin(): string {
  const base = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://www.literally.com.ng';
  return base.replace(/\/+$/, '');
}

/* ------------------------------------------------------------------ */
/* Canonical & Share URL builders                                     */
/* ------------------------------------------------------------------ */

/** Builds clean, SEO-rich share URLs formatted as /:type/:author/:title-:id for indexing by author & topic */
export function getPostShareUrl(
  postOrId: PostShareInput | string,
  typeArg?: string | null
): string {
  const base = origin();
  if (typeof postOrId === 'string') {
    const postId = postOrId;
    const postType = (typeArg || 'post').toLowerCase();
    const route = postType === 'poem' ? 'poem' : postType === 'essay' ? 'essay' : postType === 'article' ? 'article' : 'post';
    return `${base}/#/${route}/${postId}`;
  }
  const post = postOrId;
  const author = getAuthorDisplayName(post);
  const rawType = (post.type || typeArg || 'post').toLowerCase();
  const route = rawType === 'poem' ? 'poem' : rawType === 'essay' ? 'essay' : rawType === 'article' ? 'article' : 'post';
  const authorSlug = slugify(author) || 'author';
  const titleSlug = slugify(post.title) || 'piece';

  return `${base}/#/${route}/${titleSlug}/${authorSlug}/${post.id}`;
}

export function getPoemShareUrl(
  poemOrId: { id: string; title?: string | null; author_name?: string | null } | string
): string {
  const base = origin();
  if (typeof poemOrId === 'string') {
    return `${base}/#/poem/${poemOrId}`;
  }
  const author = getAuthorDisplayName(poemOrId);
  const authorSlug = slugify(author) || 'author';
  const titleSlug = slugify(poemOrId.title) || 'poem';
  return `${base}/#/poem/${titleSlug}/${authorSlug}/${poemOrId.id}`;
}

export function getBookShareUrl(
  bookOrId: BookShareInput | string,
  chapterIndex?: number
): string {
  const base = origin();
  if (typeof bookOrId === 'string') {
    if (chapterIndex !== undefined && chapterIndex !== null) {
      return `${base}/#/read/${bookOrId}?chapter=${chapterIndex}`;
    }
    return `${base}/#/book/${bookOrId}`;
  }
  const book = bookOrId;
  const author = getAuthorDisplayName(book);
  const slug = getBookSlug(book.title || undefined, author, book.id, {
    seriesTitle: book.series_title || undefined,
    chapterIndex
  });
  if (chapterIndex !== undefined && chapterIndex !== null) {
    return `${base}/#/read/${slug}?chapter=${chapterIndex}`;
  }
  return `${base}/#/book/${slug}`;
}

/** Returns null when no id is available — `/author/` alone is never valid. */
export function getAuthorShareUrl(name?: string | null, id?: string | null): string | null {
  if (!id) return null;
  const slug = getAuthorSlug(name || undefined, id);
  if (!slug) return null;
  return `${origin()}/#/author/${slug}`;
}

/** Canonical URLs use the same clean paths listed in the sitemap. */
export function getPostCanonicalUrl(postOrId: PostShareInput | string, typeArg?: string | null): string {
  const base = 'https://www.literally.com.ng';
  if (typeof postOrId === 'string') {
    return `${base}/post/${postOrId}`;
  }
  const post = postOrId;
  const author = getAuthorDisplayName(post);
  const rawType = (post.type || typeArg || 'post').toLowerCase();
  const route = rawType === 'poem' ? 'poem' : rawType === 'essay' ? 'essay' : rawType === 'article' ? 'article' : 'post';
  const authorSlug = slugify(author) || 'author';
  const titleSlug = slugify(post.title) || 'piece';
  return `${base}/${route}/${authorSlug}/${titleSlug}-${post.id}`;
}

export function getPoemCanonicalUrl(poemOrId: { id: string; title?: string | null; author_name?: string | null } | string): string {
  const base = 'https://www.literally.com.ng';
  if (typeof poemOrId === 'string') {
    return `${base}/poem/${poemOrId}`;
  }
  const author = getAuthorDisplayName(poemOrId);
  const authorSlug = slugify(author) || 'author';
  const titleSlug = slugify(poemOrId.title) || 'poem';
  return `${base}/poem/${authorSlug}/${titleSlug}-${poemOrId.id}`;
}

export function getBookCanonicalUrl(bookOrId: BookShareInput | string, chapterIndex?: number): string {
  const base = 'https://www.literally.com.ng';
  if (typeof bookOrId === 'string') {
    return `${base}/book/${bookOrId}`;
  }
  const book = bookOrId;
  const author = getAuthorDisplayName(book);
  const slug = getBookSlug(book.title || undefined, author, book.id, {
    seriesTitle: book.series_title || undefined,
    chapterIndex
  });
  return `${base}/book/${slug}`;
}

export function getAuthorCanonicalUrl(name?: string | null, id?: string | null): string | null {
  if (!id) return null;
  const slug = getAuthorSlug(name || undefined, id);
  return slug ? `https://www.literally.com.ng/author/${slug}` : null;
}

/** Normalizes both legacy hash URLs and clean paths to the clean canonical format. */
export function normalizeCanonicalUrl(url?: string | null): string {
  if (!url) return 'https://www.literally.com.ng/';
  const base = 'https://www.literally.com.ng';
  try {
    const parsed = new URL(url, base);
    if (parsed.hash.startsWith('#/')) {
      const hashRoute = new URL(parsed.hash.slice(1), base);
      return `${base}${hashRoute.pathname}${hashRoute.search}`;
    }
    return `${base}${parsed.pathname}${parsed.search}`;
  } catch {
    return url.startsWith('http') ? url : `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  }
}

/** Generates vanity author subdomain URL e.g. https://id.literally.com.ng */
export function getAuthorVanityUrl(handleOrId?: string | null): string {
  const clean = (handleOrId || 'author').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
  return `https://${clean || 'author'}.literally.com.ng`;
}

/* ------------------------------------------------------------------ */
/* Share copy builders                                                */
/* ------------------------------------------------------------------ */

function cleanSnippet(text?: string | null, max = 140): string {
  if (!text) return '';
  const clean = String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return '';
  return clean.length > max ? `${clean.slice(0, max).trimEnd()}...` : clean;
}

export function buildPostShareData(post: PostShareInput): ShareData {
  const author = getAuthorDisplayName(post);
  const typeLabel = post.type
    ? `${String(post.type).charAt(0).toUpperCase()}${String(post.type).slice(1)}`
    : 'Piece';
  const postTitle = post.title?.trim() || typeLabel;
  const snippet = cleanSnippet(post.content);
  return {
    title: `"${postTitle}" by ${author}`,
    text:
      `Read "${postTitle}" by ${author} on Literally — ${SITE_TAGLINE}:` +
      (snippet ? `\n"${snippet}"` : ''),
    url: getPostShareUrl(post),
    author,
    coverUrl: post.background_url || post.media_url || undefined,
    type: (post.type || 'post') as ShareData['type'],
  };
}

export function buildBookShareData(
  book: BookShareInput,
  idOverride?: string,
  chapterIndex?: number
): ShareData {
  const id = idOverride || book.id;
  const author = getAuthorDisplayName(book);
  const bookTitle = book.title?.trim() || 'Book';
  const snippet = cleanSnippet(book.summary || book.description);
  return {
    title: `"${bookTitle}" by ${author}`,
    text:
      `Read "${bookTitle}" by ${author} on Literally — ${SITE_TAGLINE}:` +
      (snippet ? `\n"${snippet}"` : ''),
    url: getBookShareUrl({ ...book, id }, chapterIndex),
    author,
    coverUrl: book.cover_url || undefined,
    type: 'book',
  };
}

export function buildAuthorShareData(
  name?: string | null,
  id?: string | null
): ShareData | null {
  const url = getAuthorShareUrl(name, id);
  if (!url || !id) return null;
  const authorName = name?.trim() || 'Writer';
  return {
    title: `${authorName} on Literally`,
    text: `Read ${authorName}'s literary works and profile on Literally — ${SITE_TAGLINE}:`,
    url,
    author: authorName,
  };
}

/* ------------------------------------------------------------------ */
/* Clipboard + unified share execution                                */
/* ------------------------------------------------------------------ */

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    console.warn("Clipboard API write failed, attempting fallback execCommand:", err);
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    textArea.setAttribute("readonly", "");
    document.body.appendChild(textArea);
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    return successful;
  } catch (fallbackErr) {
    console.error("Fallback execCommand copy failed:", fallbackErr);
    return false;
  }
};

/**
 * Tries the Android WebView bridge first (the injected app bridge takes
 * precedence over the Web Share API inside the app's WebView), then the Web
 * Share API, then the clipboard. Never throws.
 */
export async function shareOrCopy(data: ShareData): Promise<ShareOutcome> {
  const { title, url } = data;
  const text = data.text || `Read "${title}" on Literally — ${SITE_TAGLINE}:`;

  if (isAndroidWebView() && window.AndroidBridge?.shareText) {
    try {
      window.AndroidBridge.shareText(text, url);
      vibrateNative(18);
      return 'shared';
    } catch (err) {
      console.warn('Android bridge share failed. Falling back.', err);
    }
  }

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      vibrateNative(18);
      return 'shared';
    } catch (error: any) {
      if (error?.name === 'AbortError') return 'dismissed';
      console.warn('Native share failed. Falling back to clipboard.', error);
    }
  }

  if (await copyToClipboard(url)) {
    vibrateNative(14);
    return 'copied';
  }

  return 'failed';
}

/** Maps an outcome to the standard toast copy; null = stay silent. */
export function shareOutcomeToast(
  outcome: ShareOutcome
): { message: string; type: 'success' | 'error' | 'info' } | null {
  switch (outcome) {
    case 'shared':
      return { message: 'Thanks for sharing!', type: 'success' };
    case 'copied':
      return { message: 'Link copied to clipboard!', type: 'success' };
    case 'failed':
      return { message: "Couldn't share or copy the link.", type: 'error' };
    default:
      return null;
  }
}
